import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, IconButton, SvgIcon } from "@mui/material";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import * as XLSX from "xlsx";

import "./style.scss";
import { ReactComponent as CrossIcon } from "../../assets/cross.icon.svg";
import { ReactComponent as GraphPieIcon } from "../../assets/graph-pie.icon.svg";
import { ReactComponent as GraphBarIcon } from "../../assets/graph-bar.icon.svg";
import { ReactComponent as DiagramIcon } from "../../assets/diagram.icon.svg";
import { ReactComponent as EyeIcon } from "../../assets/eye.icon.svg";
import { ReactComponent as EyeOffIcon } from "../../assets/eye-off.icon.svg";
import { ReactComponent as DownloadIcon } from "../../assets/download.icon.svg";
import { ReactComponent as ColumnsIcon } from "../../assets/columns.icon.svg";
import { StatisticsContainer } from "../../common/containers/statistics/statistics.container";
import {
    fetchCubeMeasures,
    fetchCubeDimensions,
    fetchCubeListData
} from "../../api/statistics/endpoints-referrers";
import { SectionItem } from "../../common/interfaces/comps/menu-item.interface";
import { ExportFileType } from "../../common/enums/export-file-type";
import { isMobileDevice } from "../../common/utilities/platform/platform.util";
import { DynamicTable } from "../../components/table/table.comp";
import { PaginationBlock } from "../../components/pagination/pagination.comp";
import { LoaderView } from "../../components/loader/loader.comp";
import { MultipleCheckboxMenuFilter } from "../../components/menu-filter/menu-filter.comp";
import { Graph, GraphType } from "../../components/charts/charts.comp";
import { ModalTemplate } from "../../components/modal-template/modal-template.comp";
import FileDownloadView from "../admin/file-download/file-download";

interface ModelType {
    id: string;
    name: string;
}

const TivaDataModalView = ({ handleClose }: { handleClose: () => void }) => {
    const navigate = useNavigate();
    const { isReady: isStatisticsReady } = StatisticsContainer.useContainer();
    const [searchParams] = useSearchParams();
    const [pageParam] = useState(searchParams.get("tivaDataPage"));
    const [perPageParam] = useState(searchParams.get("tivaDataPerPage"));
    const [columnsParam] = useState(searchParams.get("tivaDataColumns"));
    const [payloadParam] = useState(searchParams.get("tivaDataPayload"));
    const [data] = useState<any>(payloadParam ? JSON.parse(payloadParam) : undefined);
    const [measureFilters] = useState<{ operator: string; members: any[] }>(
        data?.measureFilters ?? { operator: "and", members: [] }
    );
    const [dimensionFilters] = useState<{ operator: string; members: any[] }>(
        data?.dimensionFilters ?? { operator: "and", members: [] }
    );
    const [queryText] = useState<string>(data?.queryText ?? "");
    const [modelType] = useState<ModelType>(data?.modelType);
    const [measures, setMeasures] = useState<any[]>();
    const [dimensions, setDimensions] = useState<any[]>();
    const [selectedGroups] = useState<any[]>(data?.groups ?? []);
    const [timeDimensions] = useState<any[]>(data?.timeDimensions ?? []);
    const [selectedMeasures, setSelectedMeasures] = useState<string[]>(data?.measures ?? []);
    const [selectedDimensions, setSelectedDimensions] = useState<string[]>(
        data?.propertiesToShow ?? []
    );
    const [columns, setColumns] = useState<string[]>(
        columnsParam ? JSON.parse(columnsParam) : [...selectedDimensions, ...selectedMeasures]
    );
    const [filterList, setFilterList] = useState<SectionItem<string>[]>([]);
    const [list, setList] = useState<any[]>([]);
    const [query, setQuery] = useState<any>();
    const [annotation, setAnnotation] = useState<any>();
    const [totalCount, setTotalCount] = useState<number>();
    const [perPageList] = useState<number[]>([25, 50, 100]);
    const [perPage, setPerPage] = useState<number>(
        perPageParam && !isNaN(Number(perPageParam))
            ? Number(perPageParam)
            : perPageList.length
              ? perPageList[0]
              : 25
    );
    const [page, setPage] = useState<number>(
        pageParam && !isNaN(Number(pageParam)) ? Number(pageParam) : 1
    );
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [downloadOpen, setDownloadOpen] = useState<boolean>(false);
    const [showGraph, setShowGraph] = useState<boolean>(false);
    const [graphAvailable, setGraphAvailable] = useState<boolean>(false);
    const [selectedGraph, setSelectedGraph] = useState<GraphType>(GraphType.area);
    const [scrollIntoView, setScrollIntoView] = useState<number>();

    const updateSearchParams = useCallback(() => {
        navigate(
            `${location.pathname}?tivaDataPayload=${JSON.stringify(data)}&tivaDataColumns=${JSON.stringify(columns)}&tivaDataPage=${page}&tivaDataPerPage=${perPage}`
        );
    }, [navigate, data, columns, page, perPage]);

    useEffect(() => {
        updateSearchParams();
    }, [page, updateSearchParams]);

    useEffect(() => {
        if (!selectedMeasures.length) {
            setShowGraph(false);
        }
        setGraphAvailable(!!selectedMeasures.length);
    }, [selectedMeasures]);

    useEffect(() => {
        if (!isStatisticsReady) return;

        const cubeName = modelType?.id || "question_answer_log";

        const loadMeasuresAndDimensions = async () => {
            try {
                const [measuresResponse, dimensionsResponse] = await Promise.all([
                    fetchCubeMeasures(cubeName),
                    fetchCubeDimensions(cubeName)
                ]);
                setMeasures(measuresResponse.data || []);
                setDimensions(dimensionsResponse.data || []);
            } catch (error) {
                console.error("Failed to load measures/dimensions:", error);
                setMeasures([]);
                setDimensions([]);
            }
        };

        loadMeasuresAndDimensions();
    }, [isStatisticsReady, modelType]);

    useEffect(() => {
        if (!dimensions || !measures) return;

        setFilterList([
            {
                title: "Columns",
                list: dimensions
                    .sort((a, b) => a.shortTitle.localeCompare(b.shortTitle))
                    .map(item => ({
                        label: item.shortTitle,
                        key: item.name,
                        value: item.name
                    }))
            },
            {
                title: "Measures",
                list: measures
                    .sort((a, b) => a.shortTitle.localeCompare(b.shortTitle))
                    .map(item => ({
                        label: item.shortTitle,
                        key: item.name,
                        value: item.name
                    }))
            }
        ]);
    }, [dimensions, measures, data]);

    useEffect(() => {
        if (!measures || !dimensions) return;

        const newDimensions = [...selectedDimensions];
        const newMeasures = [...selectedMeasures];
        selectedDimensions.forEach(item => {
            if (!columns.includes(item)) {
                const index = newDimensions.indexOf(item);
                if (index !== -1) {
                    newDimensions.splice(index, 1);
                }
            }
        });
        selectedMeasures.forEach(item => {
            if (!columns.includes(item)) {
                const index = newMeasures.indexOf(item);
                if (index !== -1) {
                    newMeasures.splice(index, 1);
                }
            }
        });
        columns.forEach(column => {
            if (!selectedDimensions.includes(column) && !selectedMeasures.includes(column)) {
                if (dimensions.find(item => item.name === column)) {
                    newDimensions.push(column);
                } else if (measures.find(item => item.name === column)) {
                    newMeasures.push(column);
                }
            }
        });

        if (newDimensions.length !== selectedDimensions.length) {
            setSelectedDimensions(newDimensions);
        }
        if (newMeasures.length !== selectedMeasures.length) {
            setSelectedMeasures(newMeasures);
        }

        updateSearchParams();
    }, [columns, selectedDimensions, selectedMeasures, measures, dimensions, updateSearchParams]);

    useEffect(() => {
        if (!isStatisticsReady) return;

        setIsLoading(true);

        const params = {
            modelType: modelType?.id,
            filters: [dimensionFilters, measureFilters],
            measures: selectedMeasures,
            groups: selectedGroups,
            propsToShow: selectedDimensions,
            timeDimensions: timeDimensions,
            limit: perPage,
            startFrom: (page - 1) * perPage,
            sortBy: []
        };

        const loadData = async () => {
            try {
                const response = await fetchCubeListData(params);
                setList(response.data.list);
                setQuery(response.data.query);
                setAnnotation(response.data.annotation);
                setTotalCount(response.data.totalNumber);
            } catch (error) {
                console.error("Failed to load cube data:", error);
                setList([]);
                setTotalCount(0);
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [
        isStatisticsReady,
        measureFilters,
        dimensionFilters,
        selectedDimensions,
        selectedMeasures,
        selectedGroups,
        timeDimensions,
        modelType,
        perPage,
        page
    ]);

    const updatePerPage = (value: number) => {
        setPage(1);
        setPerPage(value);
    };

    const handleShowHideGraphClick = () => {
        setShowGraph(!showGraph);
    };

    const handleShowGraphClick = (type: GraphType) => {
        setSelectedGraph(type);
        setShowGraph(true);
    };

    const handleDownloadOpen = () => {
        setDownloadOpen(true);
    };

    const handleDownloadClose = () => {
        setDownloadOpen(false);
    };

    const handleDownloadExport = (fileName?: string, type?: ExportFileType) => {
        setDownloadOpen(false);

        if (fileName && type) exportTable(fileName, type);
    };

    const exportTable = async (fileName: string, type: ExportFileType) => {
        setIsLoading(true);

        const params = {
            modelType: modelType?.id,
            filters: [dimensionFilters, measureFilters],
            measures: selectedMeasures,
            groups: selectedGroups,
            propsToShow: selectedDimensions,
            timeDimensions: timeDimensions,
            limit: totalCount,
            startFrom: 0,
            sortBy: []
        };

        try {
            const response = await fetchCubeListData(params);
            const result = response.data;
            const exportColumns = columns.map(column => getTitle(column, result.annotation));
            const exportList = result.list
                .map((item: any) =>
                    columns.map(key => [getTitle(key, result.annotation), item[key]])
                )
                .map((item: any) => Object.fromEntries(item));

            switch (type) {
                case ExportFileType.pdf:
                    exportPDF(fileName, exportList, exportColumns);
                    break;
                case ExportFileType.csv:
                    exportCSV(fileName, exportList, exportColumns);
                    break;
                case ExportFileType.xls:
                    exportXLS(fileName, exportList);
                    break;
            }
        } catch (error) {
            console.error("Failed to export data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const getTitle = (key: string, annotation: any) =>
        annotation?.dimensions[key]?.shortTitle ??
        annotation?.measures[key]?.shortTitle ??
        parseTitle(key);

    const parseTitle = (key: string) =>
        (key.split(".").pop() ?? key)
            .split(/[a-z](?=[A-Z])/g)
            .join(" ")
            .toLowerCase()
            .firstLetterToUppercase();

    const exportPDF = (fileName: string, list: any[], columns: string[]) => {
        const doc = new jsPDF();
        // Document-level accessibility metadata: a title (announced by AT / shown
        // in the viewer tab instead of the raw filename) and the content language.
        // jspdf-autotable does not emit a tagged (PDF/UA) structure tree, so the
        // table's reading order is not exposed to assistive tech beyond the visual
        // grid — see the VPAT note on generated exports.
        doc.setProperties({ title: fileName });
        doc.setLanguage("en");
        autoTable(doc, {
            head: [columns],
            body: list.map(item => Object.values(item)),
            headStyles: {
                fontSize: 8,
                fontStyle: "bold",
                textColor: [66, 89, 118],
                fillColor: [218, 225, 237]
            },
            styles: {
                fontSize: 8,
                fontStyle: "normal",
                textColor: [66, 89, 118],
                fillColor: [255, 255, 255]
            },
            alternateRowStyles: {
                fillColor: [246, 247, 249]
            }
        });
        doc.save(`${fileName}.pdf`);
    };

    const exportCSV = (fileName: string, list: any[], columns: string[]) => {
        const csvString = [columns, ...list.map(item => Object.values(item))]
            .map(row => row.join(","))
            .join("\n");

        const blob = new Blob([csvString], { type: "text/csv" });

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${fileName}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const exportXLS = (fileName: string, list: any[]) => {
        const worksheet = XLSX.utils.json_to_sheet(list);
        const workbook = XLSX.utils.book_new();
        // Title metadata so the workbook is identifiable to assistive tech; the
        // first row is the column-header row (from the object keys).
        workbook.Props = { Title: fileName };
        XLSX.utils.book_append_sheet(workbook, worksheet, "MBA Analytics");

        XLSX.writeFile(workbook, `${fileName}.xlsx`);
    };

    return (
        <div className="tiva-data-content">
            <div className="tiva-data-header">
                <div className="tiva-data-header-actions">
                    <Button
                        className="tiva-data-header-actions__button primary-button--icon-first"
                        variant="text"
                        startIcon={
                            <SvgIcon
                                className="primary-button--icon-first__icon"
                                component={DownloadIcon}
                                inheritViewBox
                            />
                        }
                        onClick={handleDownloadOpen}
                    >
                        <span className="primary-button--icon-first__text">Download as File</span>
                    </Button>
                    <Button
                        className="tiva-data-header-actions__button primary-button--icon-first"
                        style={{ width: "140px" }}
                        variant="text"
                        startIcon={
                            <SvgIcon
                                className="primary-button--icon-first__icon"
                                component={showGraph ? EyeOffIcon : EyeIcon}
                                inheritViewBox
                            />
                        }
                        disabled={!graphAvailable}
                        onClick={handleShowHideGraphClick}
                    >
                        <span className="primary-button--icon-first__text">
                            {showGraph ? "Hide Graph" : "Show Graph"}
                        </span>
                    </Button>
                    <IconButton
                        className={`tiva-data-header-actions__button ${showGraph && selectedGraph === GraphType.area ? "primary-icon-button" : "secondary-icon-button"}`}
                        onClick={() => handleShowGraphClick(GraphType.area)}
                        disabled={!graphAvailable}
                        aria-label="Area chart"
                        aria-pressed={showGraph && selectedGraph === GraphType.area}
                    >
                        <DiagramIcon />
                    </IconButton>
                    <IconButton
                        className={`tiva-data-header-actions__button ${showGraph && selectedGraph === GraphType.bar ? "primary-icon-button" : "secondary-icon-button"}`}
                        onClick={() => handleShowGraphClick(GraphType.bar)}
                        disabled={!graphAvailable}
                        aria-label="Bar chart"
                        aria-pressed={showGraph && selectedGraph === GraphType.bar}
                    >
                        <GraphBarIcon />
                    </IconButton>
                    <IconButton
                        className={`tiva-data-header-actions__button ${showGraph && selectedGraph === GraphType.pie ? "primary-icon-button" : "secondary-icon-button"}`}
                        onClick={() => handleShowGraphClick(GraphType.pie)}
                        disabled={selectedMeasures.length !== 1}
                        aria-label="Pie chart"
                        aria-pressed={showGraph && selectedGraph === GraphType.pie}
                    >
                        <GraphPieIcon />
                    </IconButton>
                </div>
                <IconButton
                    className="tiva-data-header-close"
                    onClick={handleClose}
                    aria-label="Close"
                >
                    <CrossIcon />
                </IconButton>
            </div>
            <div className="tiva-data-body">
                <div className="tiva-data-title">
                    <span className="tiva-data-title__query" role="heading" aria-level={1}>
                        {queryText ?? ""}
                    </span>
                    <div className="tiva-data-title__filter">
                        <MultipleCheckboxMenuFilter
                            label="Show Columns"
                            multipleList={filterList}
                            checkedList={columns}
                            setCheckedList={setColumns}
                            startIcon={ColumnsIcon}
                            showCounter={true}
                            rightPosition={true}
                        />
                    </div>
                </div>
                {!annotation || totalCount == undefined ? (
                    <LoaderView show={true}></LoaderView>
                ) : (
                    <div className="tiva-data-table-block">
                        <div className="tiva-data-table">
                            {showGraph && (
                                <div className="tiva-data-table-graph">
                                    <Graph
                                        type={selectedGraph}
                                        list={list}
                                        query={query}
                                        annotation={annotation}
                                        handleScrollIntoView={setScrollIntoView}
                                    />
                                </div>
                            )}
                            <div className="tiva-data-table-content">
                                <DynamicTable
                                    list={list}
                                    columns={columns}
                                    setColumns={setColumns}
                                    annotation={annotation}
                                    scrollIntoView={scrollIntoView}
                                    ariaLabel={
                                        queryText ? `${queryText} — data table` : "Data table"
                                    }
                                />
                            </div>
                            <LoaderView show={isLoading}></LoaderView>
                        </div>
                        <div className="tiva-data-pagination">
                            <PaginationBlock
                                totalCount={totalCount}
                                page={page}
                                setPage={setPage}
                                perPageList={perPageList}
                                perPage={perPage}
                                setPerPage={updatePerPage}
                                showFirstButton={!isMobileDevice()}
                                showLastButton={!isMobileDevice()}
                                siblingCount={isMobileDevice() ? 0 : undefined}
                                boundaryCount={isMobileDevice() ? 0 : undefined}
                            />
                        </div>
                    </div>
                )}
            </div>
            <ModalTemplate
                open={downloadOpen}
                width={654}
                height={404}
                handleClose={handleDownloadClose}
                ariaLabel="Download Data as File"
            >
                <FileDownloadView handleClose={handleDownloadExport} />
            </ModalTemplate>
        </div>
    );
};

export default TivaDataModalView;
